import { Component, OnInit, ViewChild, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Validators, FormControl, FormGroup, FormBuilder, ValidatorFn, ReactiveFormsModule } from '@angular/forms';
import { HeaderSectionDirective } from '../../directives/header-section.directive';
import { FooterSectionDirective } from '../../directives/footer-section.directive';
import { SessionAuthService } from '../../services/session/session-auth.service';
import { BrandService } from '../../services/brand/brand.service';
import { SharedService } from '../../services/shared.service';
import { SupportService } from '../../services/support.service';
import { WalPageService } from '../../services/session/wal-page.service';
import { DataExchangeService } from '../../services/session/data-exchange.service';
import { AccountTasksService } from '../../services/account/account-tasks.service';
import { ComponentSectionFactoryService } from '../../services/component-section-factory.service';
import { TitleCasePipe, CurrencyPipe, DatePipe } from '@angular/common';
import { ThemeDirective } from '../../directives/theme.directive';
import { ISessionDetails } from '../../interfaces/session-details';
import { NGXLogger } from 'ngx-logger';

@Component({
    selector: 'app-payment-demo',
    standalone: true,
    templateUrl: './payment-demo.component.html',
    styleUrl: './payment-demo.component.css',
    imports: [ThemeDirective, HeaderSectionDirective, ReactiveFormsModule, FooterSectionDirective, TitleCasePipe, CurrencyPipe, DatePipe]
})
export class PaymentDemoComponent implements OnInit {

  @ViewChild(HeaderSectionDirective, {static: true}) headerComponentSection!: HeaderSectionDirective;
  @ViewChild(FooterSectionDirective, {static: true}) footerComponentSection!: FooterSectionDirective;

  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly sessionAuthService = inject(SessionAuthService);
  private readonly walPageService = inject(WalPageService);
  private readonly supportService = inject(SupportService);
  private readonly brandService = inject(BrandService);
  private readonly sharedService = inject(SharedService);
  private readonly dataExchangeService = inject(DataExchangeService);
  private readonly componentSectionFactoryService = inject(ComponentSectionFactoryService);
  private readonly accountTasksService = inject(AccountTasksService);
  private readonly logger = inject(NGXLogger);
  private readonly destroyRef = inject(DestroyRef);

  region!: string;
  brandCode!: string;
  extAccountId!: string;
  sessionId!: string;
  params!: any;

  brandStyleTheme: any = {};

  paymentForm!: FormGroup;

  paymentKeyFields = {
    customerName: 'name',
    dueDate: 'duedate',
    amountDue: 'amountdue'
  };

  customerName!: string;
  amountDue!: number;
  dueDate!: string;

  customerNameVersion: number = 0;
  amountDueVersion: number = 0;
  dueDateVersion: number = 0;

  otherPaymentAmount!: string;
  otherPaymentDate!: string;
  isOtherPaymentAmountShown: boolean = false;
  isOtherPaymentDateShown: boolean = false;

  today = new Date();

  bankAccounts!: any[];
  cards!: any[];

  messageOneText!: string;
  isSuccessMessageOneShown: boolean = false;
  isDangerMessageOneShown = false;

  successMessage!: string;
  isSuccessMessageShown: boolean = false;
  isSpinnerShown = false;

  constructor(
    private fb: FormBuilder,
    ) {
  }

  ngOnInit() {
    let paymentAmountPattern = /^(?![0,.]+$)(?:0|[1-9]\d{0,2}(?:,\d{3})*|[1-9]\d*)(?:\.\d{1,2})?$/;
    // Prepopulate the payment methods
    this.bankAccounts = [
      { id: 'ba-1', nickName: 'Wells Fargo Checking ...1234'},
      { id: 'ba-2', nickName: 'Chase Checking ...6789'},
      { id: 'ba-3', nickName: 'Citizens Bank Savings ...5737'},
      { id: 'ba-4', nickName: 'Other'}
    ];
    this.cards = [
      { id: 'cd-1', nickName: 'Citi Visa ...1234'},
      { id: 'cd-2', nickName: 'Bank of America Mastercard ...6789'},
      { id: 'cd-3', nickName: 'American Express ...3579'},
      { id: 'cd-4', nickName: 'Other'}
    ];

    this.paymentForm = this.fb.group({
      otherPaymentAmount: [this.otherPaymentAmount, [Validators.pattern(paymentAmountPattern)]],
      otherPaymentDate: [this.otherPaymentDate],
      bankAccount: new FormControl(null, [Validators.required]),
      card: new FormControl(null)
    });
    this.paymentForm.setValue({
      otherPaymentAmount: 0.01,
      otherPaymentDate: this.today,
      bankAccount: null,
      card: null
    });

    // Get the parameters from the caller (Walletron)
    this.activatedRoute.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.params = params;
        this.region = params.get('r') || '';
        this.sessionId = params.get('si') || '';

        // Get session details to find out the auth token
        // this.getSessionDetails(this.region, this.sessionId);
        this.sessionAuthService.getWalPageSession(this.region, this.sessionId)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
        next: () => {
          // Retrieve account tasks session
          this.getAccountTasksSession();
        },
        error: (error) => {
          this.router.navigate(['/err']);
        }
      });
    });
  }

  // public getSessionDetails(region: string, sessionId: string) {
  //   this.walPageService.getSessionDetails(region, sessionId)
  //   .subscribe({
  //     next: (response) => {
  //       this.dataExchangeService.setSession(response);
  //       if (response) {
  //         let sessionDetails: ISessionDetails = response;
  //         // Find out the auth token
  //         if (sessionDetails.options) {
  //           const options: Map<string, string> = new Map(Object.entries(sessionDetails.options));
  //           if (options) {
  //             if (options.has("auth")) {
  //               let authToken: string = options.get('auth') || '';
  //               this.dataExchangeService.setAuthToken(authToken);
  //               // Retrieve account tasks session
  //               this.getAccountTasksSession();
  //             }
  //           }
  //         }
  //       }
  //     },
  //     error: (error) => {
  //       this.dataExchangeService.setMessageText(error);
  //       this.router.navigate(['/err']);
  //     },
  //     complete: () => {
  //     }
  //   });
  // }


  getAccountTasksSession() {
    this.accountTasksService.getAccountTasksSession(this.region, this.sessionId)
    .subscribe(response => {
      if (response) {
        // Create form header and footer
        this.brandCode = response.brandCode;
        this.extAccountId = response.extAccountId;
        this.makeHeaderAndFooter();
      }
    });
  }

  getAccountVariables() {
    this.accountTasksService.getAccountInfo(this.region, this.brandCode, this.extAccountId)
    .subscribe({
      next: (response) => {
        console.log('response', response);
        if (response == null || response.customerPassInfoList == null || response.customerPassInfoList.length < 1) {
        } else {
          let customerpasses = response.customerPassInfoList;
          let customerpass = customerpasses[0];
          let vpipFullList = customerpass.vpivInfoList || [];
          if (vpipFullList) {
            vpipFullList.forEach(item => {
//<<<<<<< walletron-pages-20241112-apple-recovery
//              if (item.variableName == this.paymentKeyFields.amountDue) this.amountDue = Number(item.variableValue);
//              if (item.variableName == this.paymentKeyFields.customerName) this.customerName = item.variableValue;
//              if (item.variableName == this.paymentKeyFields.dueDate) this.dueDate = item.variableValue;
//=======
              if (item.variableName == this.paymentKeyFields.amountDue && this.amountDueVersion < item.versionNum) {
                this.amountDue = Number(item.variableValue.replace('$',''));
                this.amountDueVersion = item.versionNum;
              }
              if (item.variableName == this.paymentKeyFields.customerName && this.customerNameVersion < item.versionNum) {
                this.customerName = item.variableValue;
                this.customerNameVersion = item.versionNum;
              }
              if (item.variableName == this.paymentKeyFields.dueDate && this.dueDateVersion < item.versionNum) {
                this.dueDate = item.variableValue;
                this.dueDateVersion = item.versionNum;
                var origDate: Date = new Date(this.dueDate);
                var today: Date = new Date();
                if (origDate.getFullYear() != today.getFullYear()) {
                  origDate.setFullYear(today.getFullYear());
                  this.dueDate = origDate.toString();
                }
              }

              // switch(item.variableName.toLowerCase()) {
              //   case 'amountdue':
              //     this.amountDue = Number(item.variableValue);
              //     break;
              //   case 'name':
              //     this.customerName = item.variableValue;
              //     break;
              //   case 'duedate':
              //     this.dueDate = item.variableValue;
              //     break;
              // }

            })
          }
        }
      },
      error: (error) => {
        },
      complete: () => {
      }
    });
  }


  onSubmitPayment(event: any) {
    this.isSpinnerShown = true;
    sleep(2000).then(() => {
      this.successMessage = 'Your payment has been successfully processed. Confirmation number is '
      + this.supportService.getRandomString(10) + '. Thank you for your business.';
      this.isSuccessMessageShown = true;
      this.isSpinnerShown = false;
    });
  }



  onChangePaymentAmount(paymentAmountChoice: string): void
  {
    if (paymentAmountChoice == 'other') {
      this.isOtherPaymentAmountShown = true;
    } else {
      this.isOtherPaymentAmountShown = false;
    }
  }

  onChangePaymentDate(paymentDateChoice: string): void
  {
    if (paymentDateChoice == 'other') {
      this.isOtherPaymentDateShown = true;
    } else {
      this.isOtherPaymentDateShown = false;
    }
  }

  onChangePaymentMethod(paymentMethodChoice: string): void
  {
    // let validators: ValidatorFn[] = [];
    // validators.push(Validators.required);
    // this.paymentForm.controls['bankAccount'].clearValidators();
    // this.paymentForm.controls['card'].clearValidators();
    // if (paymentMethodChoice == 'checking/savings') {
    //   this.paymentForm.controls['bankAccount'].setValidators(validators);
    //   this.paymentForm.patchValue({
    //     card: null
    //   });
    // } else {
    //   this.paymentForm.controls['card'].setValidators(validators);
    //   this.paymentForm.patchValue({
    //     bankAccount: null
    //   });
    // }

    let validators: ValidatorFn[] = [Validators.required];
    this.paymentForm.get('bankAccount')?.clearValidators();
    this.paymentForm.get('card')?.clearValidators();
    if (paymentMethodChoice == 'checking/savings') {
      this.paymentForm.get('bankAccount')?.setValidators(validators);
      this.paymentForm.patchValue({
        card: null
      });
    } else {
      this.paymentForm.get('card')?.setValidators(validators);
      this.paymentForm.patchValue({
        bankAccount: null
      });
    }

  }

  makeHeaderAndFooter() {
    this.brandService.getBrandInfo(this.region, this.brandCode, "NPE,WAL_PAGES")
    .subscribe(brandInfo => {
      if (brandInfo) {
        if (brandInfo.configProperties) {
          this.paymentKeyFields = JSON.parse(brandInfo.configProperties['WAL_PAGES_PAYMENTS_DEMO_KEY_FIELDS']);
        }
        // brandInfo.sectionText = 'Payment Demo';
        this.brandStyleTheme = this.sharedService.getBrandStyleScheme(brandInfo);
        this.componentSectionFactoryService.loadHeaderSection(brandInfo, this.headerComponentSection);
        this.componentSectionFactoryService.loadFooterSection(brandInfo, this.footerComponentSection);
        // Retrieve account variables to find out the customer, payment date and amount due
        this.getAccountVariables();
      }
    });
  }

  onAccountTasksDemo() {
    this.router.navigate(['/acct-tsk'], {queryParams:{ r: this.region, si: this.sessionId }});
  }

  onClose() {
    // window.open('','_parent','');
    window.open('_self');
    window.close();
  }

}

function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}
